import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons';

Chart.register(...registerables);

const AnalyticsCharts: React.FC = () => {
  const engagementChartRef = useRef<HTMLCanvasElement>(null);
  const platformChartRef = useRef<HTMLCanvasElement>(null);
  const engagementChartInstance = useRef<Chart | null>(null);
  const platformChartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    // Cleanup function to destroy existing charts
    const cleanup = () => {
      if (engagementChartInstance.current) {
        engagementChartInstance.current.destroy();
        engagementChartInstance.current = null;
      }
      if (platformChartInstance.current) {
        platformChartInstance.current.destroy();
        platformChartInstance.current = null;
      }
    };

    // Clean up any existing charts first
    cleanup();

    if (engagementChartRef.current) {
      const ctx = engagementChartRef.current.getContext('2d');
      if (ctx) {
        engagementChartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
            datasets: [{
              label: 'Engagement',
              data: [12, 19, 15, 25, 22, 18, 20],
              borderColor: '#A259FF',
              backgroundColor: 'rgba(162, 89, 255, 0.1)',
              tension: 0.4,
              fill: true
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              }
            },
            scales: {
              x: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              },
              y: {
                grid: {
                  color: 'rgba(255, 255, 255, 0.1)'
                },
                ticks: {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }
            }
          }
        });
      }
    }

    if (platformChartRef.current) {
      const ctx = platformChartRef.current.getContext('2d');
      if (ctx) {
        platformChartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Instagram', 'Facebook', 'Twitter', 'LinkedIn'],
            datasets: [{
              data: [45, 30, 15, 10],
              backgroundColor: ['#A259FF', '#00CFFD', '#2AF598', '#FF6EC7'],
              borderWidth: 0
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false
              }
            }
          }
        });
      }
    }

    // Cleanup on unmount
    return cleanup;
  }, []);

  return (
    <section id="analytics-charts" className="mb-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engagement Over Time */}
        <div className="backdrop-blur-xl bg-white/5 p-6 rounded-2xl border border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Zaman İçinde Etkileşim</h3>
            <div className="flex space-x-2">
              <button className="px-3 py-1 bg-purple-600/20 text-purple-400 text-sm rounded-lg">7G</button>
              <button className="px-3 py-1 text-white/50 text-sm rounded-lg hover:bg-white/10">30G</button>
              <button className="px-3 py-1 text-white/50 text-sm rounded-lg hover:bg-white/10">90G</button>
            </div>
          </div>
          <div className="h-64">
            <canvas ref={engagementChartRef}></canvas>
          </div>
        </div>

        {/* Platform Distribution */}
        <div className="backdrop-blur-xl bg-white/5 p-6 rounded-2xl border border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Platform Dağılımı</h3>
            <button className="text-white/50 hover:text-white transition-colors">
              <FontAwesomeIcon icon={faEllipsisH} />
            </button>
          </div>
          <div className="h-64 flex items-center justify-center">
            <canvas ref={platformChartRef}></canvas>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-purple-400 rounded-full"></div>
              <span className="text-white/70 text-sm">Instagram: 45%</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
              <span className="text-white/70 text-sm">Facebook: 30%</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              <span className="text-white/70 text-sm">Twitter: 15%</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-3 h-3 bg-pink-400 rounded-full"></div>
              <span className="text-white/70 text-sm">LinkedIn: 10%</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsCharts;
